import React, { useEffect, useState } from "react";
import Button from "@/components/atoms/Button";
import { ChevronLeft } from "lucide-react";
import LoginForm, {
  useLoginForm,
} from "@/components/organisms/Login/Form"
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Link from "@/components/atoms/Link";
import { usePage } from "@inertiajs/react";

const storedTheme = localStorage.getItem("theme");
const defaultTheme = "light";
let theme = defaultTheme;

try {
  const parsedTheme = storedTheme ? JSON.parse(storedTheme) : null;
  
  if (parsedTheme && typeof parsedTheme === "object" && parsedTheme.state && typeof parsedTheme.state.theme === "string") {
    theme = parsedTheme.state.theme;
  } else {
    throw new Error("Invalid theme format");
  }
} catch (e) {
  theme = defaultTheme;
  localStorage.setItem("theme", JSON.stringify({ state: { theme: defaultTheme } }));
}

type LoginProps = {
  success?: boolean;
  message?: string;
};

const Login = () => {
  const { props } = usePage<LoginProps>();
  console.log(props);
  const [mode, setMode] = useState<"home" | "login" | "forgotPassword" | "resendConfirmation">(
    "home"
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<{success: boolean, message: string} | null>(null);
  const [headerText, setHeaderText] = useState({
    title: "Login to your account",
    description:
      "Please log in to access your account. Select your login type below.",
  });

  const handleClientLoginClick = () => {
    setMode("login");
  };

  const handleBackClick = () => {
    if (mode === "login") {
      setMode("home");
    } else if (mode === "forgotPassword") {
      setMode("login");
    } else if (mode === "resendConfirmation") {
      setMode("login");
    }
  };

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/users/sign_in', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          user: {
            email: data?.username,
            password: data?.password,
          },
        }),
      });
      
      const responseData = await response.json();

      if (response.ok) {
        const redirectUrl = responseData.redirect_url || '/';
        window.location.href = redirectUrl;
      } else {
        setToastMessage({ success: false, message: `Error: ${responseData.message}` });
      }
    } catch (error) {
      setToastMessage({ success: false, message: `Error: ${error}` });
    } finally {
      setIsSubmitting(false);
    }

  }

  const onResetPassword = async (data: { email: string }) => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/users/password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ user: data }),
      });

      const responseData = await response.json();

      if (response.ok) {
        setToastMessage({ success: true, message: "If your email is registered in our system, you will receive password reset instructions shortly." });
      } else {
        setToastMessage({ success: false, message: responseData.error || "Failed to send reset email." });
      }
    } catch (error) {
      setToastMessage({ success: false, message: `Error: ${error}` });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const onResendConfirmation = async (data: { email: string }) => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/users/confirmation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ user: data }),
      });
 
      const responseData = await response.json();
 
      if (response.ok) {
        setToastMessage({ success: true, message: "Confirmation instructions have been resent." });
      } else {
        setToastMessage({ success: false, message: responseData.error || "Failed to send confirmation email." });
      }
    } catch (error) {
      setToastMessage({ success: false, message: `Error: ${error}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getHeaderText = () => {
    switch (mode) {
      case "login":
        return {
          title: "Login to your account",
          description:
            "Please enter your credentials into the box below.",
        };
      case "forgotPassword":
        return {
          title: "Forgot your password?",
          description: "Please enter your email to reset your password.",
        };
      case "resendConfirmation":
        return {
          title: "Confirmation instructions",
          description: "Please enter your email to resend confirmation instructions.",
        };
      default:
        return {
          title: "Login to your account",
          description:
            "Please log in to access your account. Select your login type below.",
        };
    }
  };

  useEffect(() => {
    if (toastMessage) {
      if (toastMessage.success) {
        toast.success(toastMessage.message);
      } else {
        toast.error(toastMessage.message);
      }
    }
  }, [toastMessage]);

  useEffect(() => {
    if (props.message) {
      setTimeout(() => {
        if (props.success) {
          toast.success(props.message);
        } else {
          toast.error(props.message);
        }
      }, 100);
    }
  }, [props]);

  useEffect(() => {
    setHeaderText(getHeaderText());
  }, [mode]);
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-lg h-auto rounded-lg w-2/6 p-10">
        {mode !== "home" && (
          <div className="cursor-pointer" onClick={handleBackClick}>
            <ChevronLeft />
          </div>
        )}
        {/* Header Image */}
        <div className="flex justify-center mb-16">
          <img
            src="/images/logo_mark.png"
            alt="logo"
            className="w-48"
          />
        </div>

        {/* Title */}
        <h2 className="flex justify-center text-3xl font-bold text-gray-800 mb-2">
          {headerText.title}
        </h2>
        <p className="text-start text-xl text-gray-500 mb-6">
          {headerText.description}
        </p>


        {mode === 'home' ? (<div>
          {/* Client Login Button */}
          <Button onClick={handleClientLoginClick} className="flex w-full p-7 rounded-xl items-center gap-2">
            CLIENT LOGIN
          </Button>

          {/* Divider */}
          <div className="flex items-center mt-4 mb-4">
            <hr className="flex-grow border-t border-gray-300" />
            <span className="mx-2 text-gray-500 text-sm">or</span>
            <hr className="flex-grow border-t border-gray-300" />
          </div>

          {/* Staff Login Button */}
          <Link href="/auth/azureactivedirectory">
            <Button className="flex w-full p-7 rounded-xl items-center gap-2">
              STAFF LOGIN
            </Button>
          </Link>
        </div>) : (<div>
          <LoginForm
            onSubmit={onSubmit}
            onResetPassword={onResetPassword}
            onResendConfirmation={onResendConfirmation}
            onModeChange={setMode}
            isSubmitting={isSubmitting}
            selectedMode={mode}
          />
        </div>)}
      </div>
        <ToastContainer theme={theme} position="bottom-right" />
    </div>
  );
};

export default Login;

